import { ApprovalLevelRequirementEnum, ApprovalStatusEnum, ApproverEnum } from 'shared/constants';
import { ApproverUser, ExportPriceDTO, QuoteProductGroup } from 'types/backend';
import { AddModelCurrentPricesForm, DeviationsConfirmationRow, IApproverProps } from 'types/forms';
import { determineEnduserDisplay, formatDateWithTime, formatPercent } from './formats';

export function determineApproverAndStatus(
    approverUsers: ApproverUser[] | undefined,
    tierOnePrice: number,
    deviatedPrice: number,
    priceTier: string
): IApproverProps {
    const approvalLevel = determineApprovalLevelRequirement(tierOnePrice, deviatedPrice, priceTier);
    const approvers = determineApprovers(approverUsers, approvalLevel);
    const approver: ApproverEnum | string =
        approvalLevel === ApprovalLevelRequirementEnum.NOTREQUIRED
            ? ApproverEnum.NOTREQUIRED
            : approvers.length
            ? approvers
                  .map((a) => {
                      return a.name;
                  })
                  .join(', ')
            : ApproverEnum.NOTAVAILABLE;

    return {
        approver,
        status: approver === ApproverEnum.NOTREQUIRED ? ApprovalStatusEnum.APPROVED : ApprovalStatusEnum.NEEDSAPPROVAL,
    };
}

export function determineApprovers(
    approverUsers: ApproverUser[] | undefined,
    approvalLevel: ApprovalLevelRequirementEnum
): ApproverUser[] {
    if (approvalLevel === ApprovalLevelRequirementEnum.LEVEL1) {
        return approverUsers?.filter((u) => u.islevel1approver) ?? [];
    } else if (approvalLevel === ApprovalLevelRequirementEnum.LEVEL2) {
        return approverUsers?.filter((u) => u.islevel2approver) ?? [];
    }
    return [];
}

export function determineApprovalLevelRequirement(
    tierOnePrice: number,
    deviatedPrice: number,
    priceTier: string
): ApprovalLevelRequirementEnum {
    const priceDifference = tierOnePrice - deviatedPrice;
    const priceDifferenceRounded = priceDifference.toFixed(4);
    if (Number(priceDifferenceRounded) >= 0.03) {
        return ApprovalLevelRequirementEnum.LEVEL2;
    }
    if (Number(priceDifferenceRounded) >= 0.02) {
        return ApprovalLevelRequirementEnum.LEVEL1;
    }
    return ApprovalLevelRequirementEnum.NOTREQUIRED;
}
export const determineVariance = (deviatedPrice: number, recommendedPrice: number): string => {
    return formatPercent(deviatedPrice - recommendedPrice);
};

export const generateQuoteNumber = (customerNumber: string, date: Date) => {
    return customerNumber + formatDateWithTime(date);
};

export const generateProductGroups = (rows: ExportPriceDTO[]): QuoteProductGroup[] => {
    let productGroups: QuoteProductGroup[] = [];
    let categoryNameList: string[] = [];
    rows.map((p) => p.group).forEach((g) => (!categoryNameList.includes(g) ? categoryNameList.push(g) : null));

    categoryNameList.forEach((g) => {
        const productGroup: QuoteProductGroup = { header: g, color: 'red', products: [] };
        rows.forEach((p) => {
            const roundedProduct = {
                ...p,
                pricePerOneThousand: p.pricePerOneThousand
                    ? Number(p.pricePerOneThousand).toFixed(2)
                    : p.pricePerOneThousand,
                pricePerRoll: p.pricePerRoll ? Number(p.pricePerRoll).toFixed(2) : p.pricePerRoll,
            };
            return p.group === g ? productGroup.products.push(roundedProduct) : null;
        });
        productGroups.push(productGroup);
    });

    return productGroups;
};

export const mapConfirmDeviationsFormData = (
    form: AddModelCurrentPricesForm,
    approverUsers: ApproverUser[] | undefined,
    priceTier: string
): DeviationsConfirmationRow[] => {
    return form.addedDeviations.map((row) => {
        const { approver, status } = determineApproverAndStatus(
            approverUsers,
            row.selectedProduct.tierOnePrice,
            Number(row.modelCurrentPrice.price),
            priceTier
        );
        return {
            productId: row.selectedProduct.productId,
            deviationType: row.deviationDetails.deviationType,
            endUser: determineEnduserDisplay(
                row.city,
                row.state,
                row.street,
                row.endUserDisplay,
                row.modelCurrentPrice.enduseraccountnumber
            ),
            pricingGroup: row.modelCurrentPrice.enduserpricinggroup || '',
            isOneTimeOnly: row.deviationDetails.isOneTimeOnly,
            tierOnePrice: row.selectedProduct.tierOnePrice,
            targetPrice: row.selectedProduct.targetPrice,
            marketPrice: row.selectedProduct.price,
            deviatedPrice: row.modelCurrentPrice.price,
            effectiveStartDate: row.modelCurrentPrice.startdate,
            endDate: row.modelCurrentPrice.enddate,
            status,
            approver,
        };
    });
};

export const determineApprovalLevel = (approverUsers: ApproverUser[] | undefined = [], value: string): string => {
    const requiredApprovers = approverUsers?.filter((x) => {
        return value.includes(x.name);
    });

    if (requiredApprovers.length === 0) {
        return value;
    }

    const approverLevel = requiredApprovers.reduce(
        (accum, { islevel1approver, islevel2approver }, index) => {
            if (index === 0) return { islevel1approver, islevel2approver };

            return {
                islevel1approver: accum.islevel1approver && islevel1approver,
                islevel2approver: accum.islevel2approver && islevel2approver,
            };
        },
        { islevel1approver: false, islevel2approver: false }
    );

    if (approverLevel.islevel1approver) {
        return 'Level 1 Approver';
    } else if (approverLevel.islevel2approver) {
        return 'Level 2 Approver';
    }
    return '';
};
